import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { decamelizeKeys } from "humps";
import { stringify } from "qs";
import { createWrapper } from "main/features/AxiosWrapper";
import { Calendar } from "main/types/calendar";
import { ErrorResponse } from "main/types/errorResponse";
import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction,
} from "main/utils/sliceUtil";
import { RootState } from "main/store";
import {
  format,
  parseISO,
  differenceInCalendarMonths,
  addMonths,
  subMonths,
  eachDayOfInterval,
  startOfMonth,
} from "date-fns";
import { normalize } from "normalizr";
import { calendarRatesSchema } from "main/schemas/calendarSchema";
import { API_BASE_PATH } from "main/constants/constant";
import { DEFAULT_ADULTS } from "main/constants/hotelConstants";

const key = "calendar";
const adapter = createEntityAdapter({
  selectId: (rate: Calendar.Rate) => rate.date,
  sortComparer: (a, b) => a.priceCents - b.priceCents,
});

const initialStateValues: Calendar.CalendarState = {
  error: null,
  loading: false,
  months: {},
};

const initialState = {
  hotel: adapter.getInitialState(initialStateValues),
  rooms: {},
  plans: {},
  roomPlans: {},
  guests: { adults: DEFAULT_ADULTS }, // 1部屋目のみ保持
};

export const fromDateNotFetched = (
  fromDate,
  calendarRateMonths,
  differenceInMonth,
) => {
  let paramFromDate = fromDate;
  const startOfFromDateMonth = startOfMonth(fromDate);
  // fromでfetch済みの月があれば、次の月に繰り越して再度チェック
  for (let count = 0; count <= differenceInMonth; count++) {
    // fromDateは1日ではない場合がある
    // 次の月は1日を基準に取得する
    const baseDate = count === 0 ? fromDate : startOfFromDateMonth;
    const fromDateFetched =
      calendarRateMonths &&
      calendarRateMonths[format(addMonths(baseDate, count), "yyyy-LL")];

    if (!fromDateFetched) {
      break;
    }
    paramFromDate = fromDateFetched
      ? addMonths(baseDate, count + 1)
      : paramFromDate;
    console.log(`paramFromDate: ${paramFromDate}`);
  }

  return paramFromDate;
};

export const toDateNotFetched = (
  toDate,
  calendarRateMonths,
  differenceInMonth,
) => {
  let paramToDate = toDate;

  // toでfetch済みの月があれば、前の月に繰り越して再度チェック
  for (let count = 0; count <= differenceInMonth; count++) {
    const toDateFetched =
      calendarRateMonths &&
      calendarRateMonths[format(subMonths(toDate, count), "yyyy-LL")];
    if (!toDateFetched) {
      break;
    }
    paramToDate = toDateFetched ? subMonths(toDate, count + 1) : paramToDate;
    console.log(`paramToDate: ${paramToDate}`);
  }

  return paramToDate;
};

/** Async * */
export const fetchCalendar = createAsyncThunk<
  {
    rates: Calendar.Rate[];
    roomId?: number;
    planId?: number;
    guests: any;
    fromDate: string;
    toDate: string;
  },
  {
    hotelId: string;
    param: {
      fromDate: string;
      toDate: string;
      guests: any;
      hotelRoomId?: number;
      hotelPlanId?: number;
      isOneDayTrip?: boolean;
    };
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchCalendar`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, param } = args;
    const { guests, ...restParam } = param;
    const paramStr: string = stringify(
      decamelizeKeys({ ...restParam, ...guests }),
      {
        arrayFormat: "brackets",
      },
    );
    const result = await createWrapper().get(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/calendar.json?${paramStr}`,
    );
    return {
      rates: result.data.rates,
      roomId: param.hotelRoomId,
      planId: param.hotelPlanId,
      guests: param.guests,
      fromDate: param.fromDate,
      toDate: param.toDate,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const calendarSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    clearCachedData: (state) => {
      state.hotel = initialState.hotel;
      state.rooms = initialState.rooms;
      state.plans = initialState.plans;
      state.roomPlans = initialState.roomPlans;
    },
    clearRoomsCachedData: (state) => {
      state.rooms = initialState.rooms;
    },
    clearPlansCachedData: (state) => {
      state.plans = initialState.plans;
    },
    clearRoomPlansCachedData: (state) => {
      state.roomPlans = initialState.roomPlans;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendar.fulfilled, (state, action) => {
        const fromDateObject = parseISO(action.payload.fromDate);
        const toDateObject = parseISO(action.payload.toDate);
        const differenceInMonth = differenceInCalendarMonths(
          toDateObject,
          fromDateObject,
        );
        const months = [];
        for (let count = 0; count <= differenceInMonth; count++) {
          const month = format(addMonths(fromDateObject, count), "yyyy-LL");
          months.push(month);
        }

        const targetDates = eachDayOfInterval({
          start: fromDateObject,
          end: toDateObject,
        });
        const targetDateKeys = targetDates.map((date) =>
          format(date, "yyyy-LL-dd"),
        );

        state.guests = action.payload.guests;

        const normalized = normalize(action.payload.rates, calendarRatesSchema);

        if (action.payload.roomId && action.payload.planId) {
          const roomId = action.payload.roomId;
          const planId = action.payload.planId;
          if (!state.roomPlans[roomId]) {
            state.roomPlans[roomId] = {};
          }
          if (!state.roomPlans[roomId][planId]) {
            state.roomPlans[roomId][planId] = { months: {} };
          }
          state.roomPlans[roomId][planId].ids = createTargetIds(
            targetDateKeys,
            state.roomPlans[roomId][planId].ids,
            normalized.result,
          );
          state.roomPlans[roomId][planId].entities = createTargetEntities(
            targetDateKeys,
            state.roomPlans[roomId][planId].entities,
            normalized.entities?.rates,
          );
          months.map((month) => {
            state.roomPlans[roomId][planId].months[month] = true;
          });
        } else if (action.payload.roomId) {
          const roomId = action.payload.roomId;
          const normalized = normalize(
            action.payload.rates,
            calendarRatesSchema,
          );
          if (!state.rooms[roomId]) {
            state.rooms[roomId] = { months: {} };
          }
          state.rooms[roomId].ids = createTargetIds(
            targetDateKeys,
            state.rooms[roomId].ids,
            normalized.result,
          );
          state.rooms[roomId].entities = createTargetEntities(
            targetDateKeys,
            state.rooms[roomId].entities,
            normalized.entities?.rates,
          );
          months.map((month) => {
            state.rooms[roomId].months[month] = true;
          });
        } else if (action.payload.planId) {
          const planId = action.payload.planId;
          const normalized = normalize(
            action.payload.rates,
            calendarRatesSchema,
          );
          if (!state.plans[planId]) {
            state.plans[planId] = { months: {} };
          }
          state.plans[planId].ids = createTargetIds(
            targetDateKeys,
            state.plans[planId].ids,
            normalized.result,
          );
          state.plans[planId].entities = createTargetEntities(
            targetDateKeys,
            state.plans[planId].entities,
            normalized.entities?.rates,
          );
          months.map((month) => {
            state.plans[planId].months[month] = true;
          });
        } else {
          state.hotel.ids = createTargetIds(
            targetDateKeys,
            state.hotel.ids,
            normalized.result,
          );
          state.hotel.entities = createTargetEntities(
            targetDateKeys,
            state.hotel.entities,
            normalized.entities?.rates,
          );
          months.map((month) => {
            state.hotel.months[month] = true;
          });
        }
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.hotel.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.hotel.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.hotel.error = action.payload?.error_messages;
        state.hotel.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const { selectAll: calendarRatesSelector, selectEntities } =
  adapter.getSelectors((state: RootState) => state.calendar.hotel);

export const roomCalendarSelector = createSelector(
  stateSelector,
  (state) => state.rooms,
);

export const planCalendarSelector = createSelector(
  stateSelector,
  (state) => state.plans,
);

export const roomPlansCalendarSelector = createSelector(
  stateSelector,
  (state) => state.roomPlans,
);
const emptyObject = {};
export const calendarRateEntitiesSelector = createSelector(
  [
    selectEntities,
    roomCalendarSelector,
    planCalendarSelector,
    roomPlansCalendarSelector,
    (state, options: { hotelRoomId?: number; hotelPlanId?: number }) => options,
  ],
  (entities, rooms, plans, roomPlans, options) => {
    if (options["hotelRoomId"] && options["hotelPlanId"]) {
      return (
        (roomPlans[options["hotelRoomId"]] || [])[options["hotelPlanId"]]
          ?.entities || emptyObject
      );
    }
    if (options["hotelRoomId"]) {
      return rooms[options["hotelRoomId"]]?.entities || emptyObject;
    }
    if (options["hotelPlanId"]) {
      return plans[options["hotelPlanId"]]?.entities || emptyObject;
    }
    return entities;
  },
);

export const calendarRateMonthsSelector = createSelector(
  [
    stateSelector,
    roomCalendarSelector,
    planCalendarSelector,
    roomPlansCalendarSelector,
    (state, options: { hotelRoomId?: number; hotelPlanId?: number }) => options,
  ],
  (state, rooms, plans, roomPlans, options) => {
    if (options["hotelRoomId"] && options["hotelPlanId"]) {
      return (
        (roomPlans[options["hotelRoomId"]] || [])[options["hotelPlanId"]]
          ?.months || emptyObject
      );
    }
    if (options["hotelRoomId"]) {
      return rooms[options["hotelRoomId"]]?.months || emptyObject;
    }
    if (options["hotelPlanId"]) {
      return plans[options["hotelPlanId"]]?.months || emptyObject;
    }
    return state.hotel.months;
  },
);

export const guestsSelector = createSelector(
  stateSelector,
  (state) => state.guests,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.hotel.loading,
);

// action export
export const {
  clearCachedData,
  clearRoomsCachedData,
  clearPlansCachedData,
  clearRoomPlansCachedData,
} = calendarSlice.actions;

// reducer内で使用する
const createTargetIds = (targetDateKeys: string[], prevIds, nextIds) => {
  const ids = prevIds?.filter((id) => !targetDateKeys.includes(id)) || [];
  return ids.concat(nextIds).sort();
};
const createTargetEntities = (
  targetDateKeys: string[],
  prevEntities,
  nextEntities,
) => {
  const entities = { ...prevEntities };
  targetDateKeys.map((dateKey) => {
    // 在庫なしの場合はratesにデータがないので
    // 対象のデータを一度削除してから新データがあれば書き換える
    if (entities && entities[dateKey]) {
      delete entities[dateKey];
    }
    if (nextEntities && nextEntities[dateKey]) {
      entities[dateKey] = nextEntities[dateKey];
    }
  });
  return entities;
};
